import { Injectable } from '@angular/core';

/** Uma linha do CSV, indexada pelo cabeçalho já normalizado. */
export interface CsvRow {
  [coluna: string]: string;
}

export interface CsvParseResult {
  /** Cabeçalhos normalizados (minúsculo, sem acento, espaços viram `_`). */
  headers: string[];
  /** Cabeçalhos como vieram no arquivo, na mesma ordem de `headers`. */
  headersOriginais: string[];
  rows: CsvRow[];
  /** Linha física de início de cada registro, inclusive após vazios e campos multilinha. */
  rowLines: number[];
  /** Separador detectado no arquivo. */
  delimiter: string;
}

/**
 * Leitura e escrita de CSV sem dependência externa.
 *
 * Trata os casos que aparecem na prática com planilhas brasileiras:
 * separador `;` (padrão do Excel em pt-BR) ou `,`, campos entre aspas com
 * separador ou quebra de linha dentro, aspas duplicadas como escape, BOM no
 * início do arquivo e quebras de linha CRLF.
 */
@Injectable({
  providedIn: 'root',
})
export class CsvService {
  /** Lê um arquivo selecionado pelo usuário e devolve o conteúdo como texto. */
  async readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file, 'UTF-8');
    });
  }

  /** Lê o arquivo e já devolve o conteúdo interpretado. */
  async parseFile(file: File): Promise<CsvParseResult> {
    const texto = await this.readFile(file);
    return this.parse(texto);
  }

  parse(conteudo: string): CsvParseResult {
    const texto = this.removeBom(conteudo);

    if (!texto.trim()) {
      return { headers: [], headersOriginais: [], rows: [], rowLines: [], delimiter: ';' };
    }

    const delimiter = this.detectDelimiter(texto);
    const linhas = this.split(texto, delimiter);

    if (!linhas.length) {
      return { headers: [], headersOriginais: [], rows: [], rowLines: [], delimiter };
    }

    const headersOriginais = (linhas[0]?.fields ?? []).map((h) => h.trim());
    const headers = headersOriginais.map((h) => this.normalizeHeader(h));
    if (new Set(headers).size !== headers.length)
      throw Error('O CSV contém cabeçalhos duplicados após normalização. Corrija as colunas.');

    const rows: CsvRow[] = [];
    const rowLines: number[] = [];

    for (let i = 1; i < linhas.length; i++) {
      const campos = linhas[i]?.fields ?? [];

      // Ignora linha totalmente vazia (comum no fim do arquivo).
      if (campos.every((campo) => campo.trim() === '')) {
        continue;
      }

      if (campos.length > headers.length)
        throw Error(
          `O CSV contém campos excedentes na linha ${linhas[i]!.line}. Corrija as colunas.`,
        );
      const row: CsvRow = {};
      headers.forEach((header, indice) => {
        row[header] = (campos[indice] ?? '').trim();
      });
      rows.push(row);
      rowLines.push(linhas[i]!.line);
    }

    return { headers, headersOriginais, rows, rowLines, delimiter };
  }

  /**
   * Procura correspondência exata entre cabeçalhos normalizados e um dos
   * nomes aceitos. Devolve o cabeçalho normalizado ou `null`.
   *
   * Permite que o mesmo arquivo funcione com "numero_pedido", "Número do Pedido"
   * ou "pedido", sem exigir um layout único do usuário.
   */
  findColumn(headers: string[], aceitos: string[]): string | null {
    const alvos = new Set(aceitos.map((nome) => this.normalizeHeader(nome)));
    const encontrados = headers.filter((header) => alvos.has(header));
    if (encontrados.length > 1)
      throw Error(
        `O CSV contém colunas ambíguas: ${encontrados.join(', ')}. Mantenha apenas uma delas.`,
      );
    return encontrados[0] ?? null;
  }

  /** Normaliza um cabeçalho: minúsculo, sem acento, espaços e hífens viram `_`. */
  normalizeHeader(valor: string): string {
    return valor
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }

  /** Monta o conteúdo de um CSV a partir dos cabeçalhos e das linhas. */
  build(headers: string[], rows: CsvRow[], delimiter = ';'): string {
    const linhas = [headers.map((header) => this.escape(header, delimiter)).join(delimiter)];

    rows.forEach((row) => {
      const campos = headers.map((header) => this.escape(row[header] ?? '', delimiter));
      linhas.push(campos.join(delimiter));
    });

    return linhas.join('\r\n');
  }

  /**
   * Baixa um CSV no navegador. O BOM inicial faz o Excel abrir o arquivo em
   * UTF-8 e preservar os acentos.
   */
  download(nomeArquivo: string, conteudo: string): void {
    const bom = String.fromCharCode(0xfeff);
    const blob = new Blob([bom + conteudo], { type: 'text/csv;charset=utf-8;' });
    const blobUrl = window.URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = blobUrl;
    anchor.download = nomeArquivo;
    document.body.appendChild(anchor);
    anchor.click();

    setTimeout(() => {
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  }

  private removeBom(texto: string): string {
    return texto.charCodeAt(0) === 0xfeff ? texto.slice(1) : texto;
  }

  /**
   * Detecta o separador contando ocorrências fora de aspas na primeira linha.
   * Empate ou ausência resolve para `;`, o padrão do Excel em português.
   */
  private detectDelimiter(texto: string): string {
    const candidatos = [';', ',', '\t'];
    const primeiraLinha = this.primeiraLinhaForaDeAspas(texto);

    let escolhido = ';';
    let maior = 0;

    candidatos.forEach((candidato) => {
      const total = this.contaForaDeAspas(primeiraLinha, candidato);
      if (total > maior) {
        maior = total;
        escolhido = candidato;
      }
    });

    return escolhido;
  }

  private primeiraLinhaForaDeAspas(texto: string): string {
    let dentroDeAspas = false;

    for (let i = 0; i < texto.length; i++) {
      const char = texto[i];

      if (char === '"') {
        dentroDeAspas = !dentroDeAspas;
      } else if ((char === '\n' || char === '\r') && !dentroDeAspas) {
        return texto.slice(0, i);
      }
    }

    return texto;
  }

  private contaForaDeAspas(linha: string, alvo: string): number {
    let dentroDeAspas = false;
    let total = 0;

    for (const char of linha) {
      if (char === '"') {
        dentroDeAspas = !dentroDeAspas;
      } else if (char === alvo && !dentroDeAspas) {
        total++;
      }
    }

    return total;
  }

  /** Quebra o texto em linhas de campos, respeitando aspas. */
  private split(texto: string, delimiter: string): { fields: string[]; line: number }[] {
    const linhas: { fields: string[]; line: number }[] = [];
    let campos: string[] = [],
      campo = '',
      dentroDeAspas = false,
      aspasFechadas = false;
    let line = 1,
      recordLine = 1;
    for (let i = 0; i < texto.length; i++) {
      const char = texto[i];
      if (dentroDeAspas) {
        if (char === '"') {
          if (texto[i + 1] === '"') {
            campo += '"';
            i++;
          } else {
            dentroDeAspas = false;
            aspasFechadas = true;
          }
        } else {
          campo += char;
          if (char === '\n' || (char === '\r' && texto[i + 1] !== '\n')) line++;
        }
        continue;
      }
      if (char === '"') {
        if (campo !== '' || aspasFechadas) throw Error('O CSV contém aspas em posição inválida.');
        dentroDeAspas = true;
      } else if (char === delimiter) {
        campos.push(campo);
        campo = '';
        aspasFechadas = false;
      } else if (char === '\n' || char === '\r') {
        if (char === '\r' && texto[i + 1] === '\n') i++;
        campos.push(campo);
        linhas.push({ fields: campos, line: recordLine });
        campos = [];
        campo = '';
        aspasFechadas = false;
        recordLine = ++line;
      } else {
        if (aspasFechadas) throw Error('O CSV contém texto após a aspa de fechamento.');
        campo += char;
      }
    }
    if (dentroDeAspas) throw Error('O CSV contém um campo com aspas não fechadas.');
    if (campo !== '' || campos.length) {
      campos.push(campo);
      linhas.push({ fields: campos, line: recordLine });
    }
    return linhas;
  }

  private escape(valor: string, delimiter: string): string {
    // Aspas CSV não impedem que o Excel execute fórmulas vindas de campos livres.
    if (/^[\s\u0000-\u001f]*[=+@-]/.test(valor) || /^[\t\r\n]/.test(valor)) {
      valor = "'" + valor;
    }
    const precisaAspas =
      valor.includes(delimiter) ||
      valor.includes('"') ||
      valor.includes('\n') ||
      valor.includes('\r');

    return precisaAspas ? `"${valor.replace(/"/g, '""')}"` : valor;
  }
}
